/**
 * Bring the subscription socket back after a back/forward-cache restore.
 *
 * A bfcached page is FROZEN, not unloaded. The browser closes its WebSockets
 * and suspends its timers, then hands the same JavaScript heap back on the way
 * in — so graphql-ws wakes holding a socket that will never emit again, and
 * only discovers it when its next retry fires, up to 30 seconds of backoff
 * away. For that whole window the schedule shows "Live calendar updates are
 * unavailable" over data that is quietly going stale, and the console fills
 * with connect attempts that failed while the page was frozen
 * (`WebSocket connection to … failed: Page entered Back-Forward Cache`).
 *
 * `terminate()` is graphql-ws's documented answer to exactly this — "the
 * WebSocket is stuck and not emitting any events" — and is explicitly NOT
 * fatal: it drops the dead socket and a retry follows immediately, resetting
 * the backoff. `RealtimeService` then sees `connected` and full-refetches, so
 * the surface catches up on whatever changed while the tab was away.
 *
 * ONLY `pageshow`, and only when `persisted`. The matching `pagehide` half
 * would make the page bfcache-ELIGIBLE by closing the socket on the way out,
 * but `pagehide` also fires on an ordinary navigation away and `persisted` is
 * unreliable there, so it would churn a working connection for a page that was
 * never frozen. Recovering on the way back in costs nothing when the page was
 * never cached, because the handler simply does not run.
 *
 * Its own file so it can be tested without constructing the Apollo provider
 * tree — the part worth testing is "which events act, and which do not".
 */
export interface WsTerminable {
  terminate(): void;
}

/**
 * @returns an unsubscribe function; the app-level caller never needs it, but a
 *   test does, and a listener with no way off is how a spec leaks into the next.
 */
export function recoverWsOnBfcacheRestore(
  client: WsTerminable,
  target: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> | undefined = typeof window ===
  'undefined'
    ? undefined
    : window,
): () => void {
  if (!target) return () => undefined;
  const onShow = (event: Event): void => {
    // `persisted` is the whole test: false means an ordinary load, where the
    // socket is either already connecting or about to, and terminating it
    // would cancel a healthy attempt.
    if (!(event as PageTransitionEvent).persisted) return;
    client.terminate();
  };
  target.addEventListener('pageshow', onShow);
  return () => target.removeEventListener('pageshow', onShow);
}
